import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuthContext } from '../../store/@system/auth'
import { Spinner } from '../../components/@system/Loading'
import { ProtectedRoute } from '../../components/@system/ProtectedRoute'
import { OgMeta } from '../../components/@system/OgMeta'
import { info } from '@/config'
import { mergeRoutes, filterRoutesByModules } from './utils'
import { customRoutes } from '../@custom'
import { isModuleEnabled } from '../../../config/@system/modules'
import manifest from './manifest.json'

// Feature modules (brand.json `modules`): routes below carry `module: '<key>'`
// and are dropped by filterRoutesByModules() when that module is off. With
// selfRegistration off, /register and /signup land on the login tab.
const REGISTER_TARGET = isModuleEnabled('selfRegistration') ? '/auth?tab=register' : '/auth'

// Public critical path — landing, pricing, sign-in, the blog index and the blog article ship in
// the MAIN bundle. These are the first screens a visitor paints and their LCP element is text
// rendered by React: a lazy chunk on that path costs one extra round trip (main.js → page chunk →
// paint) that the browser cannot preload because it only discovers the chunk at runtime. Measured
// on the Lighthouse gate (mobile, simulated slow 4G): lazy = LCP 2.6–2.8 s (budget 2.5 s), eager =
// under budget. They are also the routes the server renders to HTML (manifest.json `ssr: true`,
// client/src/entry-server.jsx): a React.lazy() boundary on that path would render its Suspense
// fallback into the server HTML instead of the page. Every other page stays lazy. LandingPage
// delegates through @custom — products customise by editing
// pages/static/@custom/LandingPage/index.jsx (not this file). See docs/SCALABILITY.md §7.
import { LandingPage } from '../../pages/static/@system/LandingPage'
import { PricingPage } from '../../pages/static/@system/PricingPage'
import { AuthPage } from '../../pages/static/@system/AuthPage'
import { BlogPage } from '../../pages/static/@system/BlogPage'
import { BlogPostPage } from '../../pages/static/@system/BlogPostPage'

// Static / marketing pages — grouped into "pages-static" chunk

const AboutPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/AboutPage').then((m) => ({ default: m.AboutPage }))
)

const ArticlePage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ArticlePage').then((m) => ({ default: m.ArticlePage }))
)

const CareersPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/CareersPage').then((m) => ({ default: m.CareersPage }))
)

const ChangelogPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ChangelogPage').then((m) => ({ default: m.ChangelogPage }))
)

const ContactPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ContactPage').then((m) => ({ default: m.ContactPage }))
)

const ConversionPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ConversionPage').then((m) => ({ default: m.ConversionPage }))
)

const CookiePolicyPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/CookiePolicyPage').then((m) => ({ default: m.CookiePolicyPage }))
)

const DocsPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/DocsPage').then((m) => ({ default: m.DocsPage }))
)

const DPAPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/DPAPage').then((m) => ({ default: m.DPAPage }))
)

const ErrorPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ErrorPage').then((m) => ({ default: m.ErrorPage }))
)

const ForgotPasswordPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
)

const HelpCenterPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage }))
)

const NotFoundPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
)

const OnboardingPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/OnboardingPage').then((m) => ({ default: m.OnboardingPage }))
)

const PrivacyPolicyPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage }))
)

const RefundPolicyPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/RefundPolicyPage').then((m) => ({ default: m.RefundPolicyPage }))
)

const ResetPasswordPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
)

const RoadmapPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/RoadmapPage').then((m) => ({ default: m.RoadmapPage }))
)

const TermsPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/TermsPage').then((m) => ({ default: m.TermsPage }))
)

const TwoFactorVerifyPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/TwoFactorVerifyPage').then((m) => ({ default: m.TwoFactorVerifyPage }))
)

const VerifyEmailPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
)

const ThankYouPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/ThankYouPage').then((m) => ({ default: m.ThankYouPage }))
)

const UnsubscribePage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '../../pages/static/@system/UnsubscribePage').then((m) => ({ default: m.UnsubscribePage }))
)

// App pages (auth required) — grouped into "pages-app" chunk
const ActivityPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/ActivityPage').then((m) => ({ default: m.ActivityPage }))
)

const AdminPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/AdminPage').then((m) => ({ default: m.AdminPage }))
)

const ApiKeysPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage }))
)

const BillingPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/BillingPage').then((m) => ({ default: m.BillingPage }))
)

const BlogManagementPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/BlogManagementPage').then((m) => ({ default: m.BlogManagementPage }))
)

const CancellationsPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/AdminPage/CancellationsPage').then((m) => ({ default: m.CancellationsPage }))
)

const HomePage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/HomePage').then((m) => ({ default: m.HomePage }))
)

const IntegrationsPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage }))
)

const MobileResponsiveDemo = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/MobileResponsiveDemo').then((m) => ({ default: m.MobileResponsiveDemo }))
)

const SettingsPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)

const TeamsPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/TeamsPage').then((m) => ({ default: m.TeamsPage }))
)

const UXDemoPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/UXDemoPage').then((m) => ({ default: m.UXDemoPage }))
)

const WebhooksPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/WebhooksPage').then((m) => ({ default: m.WebhooksPage }))
)

const StaffPage = lazy(() =>
  import(/* webpackChunkName: "pages-app" */ '../../pages/app/@system/AdminPage/StaffPage').then((m) => ({ default: m.StaffPage }))
)

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner />
    </div>
  )
}

/**
 * Redirects authenticated users away from /auth back to /app
 * @param {WithChildren} props
 */
function GuestRoute({ children }) {
  const { isAuthenticated, loading } = useAuthContext()
  if (loading) return <PageFallback />
  if (isAuthenticated) return <Navigate to="/app" replace />
  return <>{children}</>
}

// @system route definitions as data — products override via @custom/index.jsx + mergeRoutes()
// Admin routes carry `permission` (mirrors requirePermission() on the API; roles are data) —
// `role="admin"` is the legacy gate and still works for products that declared it in @custom.
/** @type {import('./utils').RouteDef[]} */
const systemRoutes = [
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <Navigate to="/auth" replace /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/refund-policy', element: <RefundPolicyPage /> },
  { path: '/cookies', element: <CookiePolicyPage /> },
  { path: '/dpa', element: <DPAPage /> },
  { path: '/pricing', element: <PricingPage />, module: 'billing' },
  { path: '/help', element: <HelpCenterPage /> },
  { path: '/help/:slug', element: <ArticlePage /> },
  { path: '/blog', element: <BlogPage />, module: 'blog' },
  { path: '/blog/:slug', element: <BlogPostPage />, module: 'blog' },
  { path: '/about', element: <AboutPage /> },
  { path: '/changelog', element: <ChangelogPage /> },
  { path: '/roadmap', element: <RoadmapPage /> },
  { path: '/careers', element: <CareersPage /> },
  { path: '/docs', element: <DocsPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/conversion', element: <ConversionPage /> },
  { path: '/thank-you', element: <ThankYouPage /> },
  { path: '/unsubscribe', element: <UnsubscribePage /> },
  { path: '/onboarding', element: <OnboardingPage />, module: 'onboarding' },
  { path: '/2fa/verify', element: <TwoFactorVerifyPage /> },
  { path: '/error', element: <ErrorPage /> },
  { path: '/auth', element: <GuestRoute><AuthPage /></GuestRoute> },
  { path: '*', element: <NotFoundPage /> },
  { path: '/register', element: <Navigate to={REGISTER_TARGET} replace /> },
  { path: '/signup', element: <Navigate to={REGISTER_TARGET} replace /> },
  { path: '/dashboard', element: <Navigate to="/app" replace /> },
  { path: '/dashboard/*', element: <Navigate to="/app" replace /> },
  { path: '/cookie-policy', element: <Navigate to="/cookies" replace /> },
  // "Features" in the header is the landing anchor; the manifest declared this redirect but the router
  // never had it, so /features was a 404 on every site (dev-rankradar, 2026-10-12).
  { path: '/features', element: <Navigate to="/#features" replace /> },
  { path: '/app/activity', element: <ProtectedRoute><ActivityPage /></ProtectedRoute> },
  { path: '/app/admin', element: <ProtectedRoute permission="platform.admin"><AdminPage /></ProtectedRoute> },
  { path: '/app/admin/staff', element: <ProtectedRoute permission="staff.manage"><StaffPage /></ProtectedRoute> },
  { path: '/app/api-keys', element: <ProtectedRoute><ApiKeysPage /></ProtectedRoute>, module: 'apiKeys' },
  { path: '/app/billing', element: <ProtectedRoute><BillingPage /></ProtectedRoute>, module: 'billing' },
  { path: '/app/admin/blog', element: <ProtectedRoute permission="content.write"><BlogManagementPage /></ProtectedRoute>, module: 'blog' },
  { path: '/app', element: <ProtectedRoute><HomePage /></ProtectedRoute> },
  { path: '/app/home', element: <ProtectedRoute><HomePage /></ProtectedRoute> },
  { path: '/app/admin/cancellations', element: <ProtectedRoute permission="billing.read"><CancellationsPage /></ProtectedRoute>, module: 'billing' },
  { path: '/app/integrations', element: <ProtectedRoute permission="platform.admin"><IntegrationsPage /></ProtectedRoute> },
  { path: '/app/mobile-demo', element: <ProtectedRoute><MobileResponsiveDemo /></ProtectedRoute> },
  { path: '/app/settings', element: <ProtectedRoute><SettingsPage /></ProtectedRoute> },
  { path: '/app/teams', element: <ProtectedRoute><TeamsPage /></ProtectedRoute>, module: 'teams' },
  { path: '/app/ux-demo', element: <ProtectedRoute><UXDemoPage /></ProtectedRoute> },
  { path: '/app/webhooks', element: <ProtectedRoute><WebhooksPage /></ProtectedRoute>, module: 'webhooks' },
]

// Routes the server renders to HTML (manifest.json `ssr: true`). They are eagerly imported above
// and render WITHOUT a Suspense boundary: React hydrates a boundary's server HTML lazily, after the
// root commit, and abandons it (client re-render → the second, later LCP paint SSR exists to
// remove) the moment any provider updates state before that deferred pass — "This Suspense
// boundary received an update before it finished hydrating". Every other route keeps its own
// boundary so a lazy chunk (and any lazy @custom route) still shows PageFallback while loading.
const SSR_PATHS = new Set(manifest.static.filter((r) => r.ssr === true).map((r) => r.path))

/** Page component name per manifest path ("PrivacyPolicyPage") — the default <title> source. */
const PAGE_BY_PATH = new Map(manifest.static.map((r) => [r.path, r.page]))

/**
 * Default document title for a public route: "Privacy Policy · Brand"; the home page is the brand
 * name alone (index.html carries the tagline variant for crawlers).
 * @param {string} path
 * @returns {string | undefined}  undefined → OgMeta falls back to info.name
 */
export function defaultPageTitle(path) {
  const label = pageLabel(path)
  return label ? `${label} · ${info.name}` : undefined
}

/**
 * Titles people read, where the component name is not one: "Auth · RankRadar" was the sign-in page's title
 * (RankRadar e2e 2026-10-12 #40). The rest keep the humanised component name.
 * @type {Record<string, string>}
 */
const PAGE_LABELS = {
  AuthPage: 'Sign in',
  TwoFactorVerifyPage: 'Two-step verification',
  ForgotPasswordPage: 'Forgot password',
  ResetPasswordPage: 'Reset password',
  VerifyEmailPage: 'Verify your e-mail',
  DPAPage: 'Data processing agreement',
  HelpCenterPage: 'Help',
  CookiePolicyPage: 'Cookie policy',
  PrivacyPolicyPage: 'Privacy policy',
  TermsPage: 'Terms of service',
  RefundPolicyPage: 'Refund policy',
  DocsPage: 'Documentation',
  ThankYouPage: 'Thank you',
  UnsubscribePage: 'Unsubscribe',
  ConversionPage: 'Get started',
}

/** The page's label: the override above, else the humanised component name; undefined for home / catch-all. @param {string} path */
export function pageLabel(path) {
  if (path === '/' || path === '*') return undefined
  const page = PAGE_BY_PATH.get(path)
  if (!page) return undefined
  return PAGE_LABELS[page] || page.replace(/Page$/, '').replace(/([a-z\d])([A-Z])/g, '$1 $2')
}

/**
 * One description per public page (#37: every page carried the home description): what the page is, then
 * what the product is. The server's lib/@system/routeMeta says the same for crawlers.
 * @param {string} path
 * @returns {string | undefined}  undefined → OgMeta's brand default (home)
 */
export function defaultPageDescription(path) {
  const label = pageLabel(path)
  if (!label) return undefined
  const what = info.metaDescription || info.tagline || info.description || ''
  return what ? `${label} of ${info.name}. ${what.replace(/[.!?]+$/, '')}.` : `${label} of ${info.name}.`
}

/**
 * Every public (non-/app) route mounts the launch-contract SEO defaults — <title>, description,
 * og:image, twitter:card from brand.json (components/@system/OgMeta) — BEFORE the page element, so
 * a page that renders its own <OgMeta> (LandingPage, BlogPostPage) still wins: sibling effects run
 * in order and the page's is later. Rules 6 + 7 of docs/LAUNCH-CONTRACT (Orkosi).
 * @param {{ path: string, element: import('react').ReactNode }} route
 * @returns {import('react').ReactNode}
 */
function routeElement(route) {
  const page = SSR_PATHS.has(route.path)
    ? route.element
    : <Suspense fallback={<PageFallback />}>{route.element}</Suspense>
  if (route.path === '/app' || route.path.startsWith('/app/')) return page
  return (
    <>
      <OgMeta title={defaultPageTitle(route.path)} description={defaultPageDescription(route.path)} />
      {page}
    </>
  )
}

export function AppRoutes() {
  const routes = mergeRoutes(filterRoutesByModules(systemRoutes), customRoutes)
  return (
    <Routes>
      {routes.map((r) => (
        <Route key={r.path} path={r.path} element={routeElement(r)} />
      ))}
    </Routes>
  )
}
